/**
 * DateTime Tracking Module - Frontend
 * 
 * Tracks DateTime operations for monitoring and debugging:
 * - Conversion operations
 * - Errors
 * - Timezone detection
 * - Performance metrics
 */

class DateTimeTracker {
  constructor() {
    this.stats = {
      conversions: 0,
      errors: 0,
      timezoneDetections: 0,
      cacheHits: 0,
      cacheMisses: 0,
      operations: []
    };
    
    this.maxOperations = 100; // Keep last 100 operations
    this.enabled = true;
    
    // Performance tracking
    this.performanceMetrics = {
      conversionTimes: [],
      formatTimes: [],
      parseTimes: []
    };
  }
  
  /**
   * Track a conversion operation
   */
  trackConversion(operation, duration, success = true, error = null) {
    if (!this.enabled) return;
    
    this.stats.conversions++;
    
    if (success) {
      this.performanceMetrics.conversionTimes.push(duration);
      // Keep only last 1000 measurements
      if (this.performanceMetrics.conversionTimes.length > 1000) {
        this.performanceMetrics.conversionTimes.shift();
      }
    } else {
      this.stats.errors++;
      this.trackError('conversion', error, { operation, duration });
    }
    
    this._addOperation({
      type: 'conversion',
      operation,
      duration,
      success,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * Track a formatting operation
   */
  trackFormat(operation, duration, success = true, error = null) {
    if (!this.enabled) return;
    
    if (success) {
      this.performanceMetrics.formatTimes.push(duration);
      if (this.performanceMetrics.formatTimes.length > 1000) {
        this.performanceMetrics.formatTimes.shift();
      }
    } else {
      this.stats.errors++;
      this.trackError('format', error, { operation, duration });
    }
    
    this._addOperation({
      type: 'format',
      operation,
      duration,
      success,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * Track a parsing operation
   */
  trackParse(operation, duration, success = true, error = null) {
    if (!this.enabled) return;
    
    if (success) {
      this.performanceMetrics.parseTimes.push(duration);
      if (this.performanceMetrics.parseTimes.length > 1000) {
        this.performanceMetrics.parseTimes.shift();
      }
    } else {
      this.stats.errors++;
      this.trackError('parse', error, { operation, duration });
    }
    
    this._addOperation({
      type: 'parse',
      operation,
      duration,
      success,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * Track timezone detection
   */
  trackTimezoneDetection(timezone, method) {
    if (!this.enabled) return;
    
    this.stats.timezoneDetections++;
    
    this._addOperation({
      type: 'timezone_detection',
      timezone,
      method,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * Track cache hit
   */
  trackCacheHit(type) {
    if (!this.enabled) return;
    
    this.stats.cacheHits++;
  }
  
  /**
   * Track cache miss
   */
  trackCacheMiss(type) {
    if (!this.enabled) return;
    
    this.stats.cacheMisses++;
  }
  
  /**
   * Track an error
   */
  trackError(type, error, context = {}) {
    if (!this.enabled) return;
    
    this.stats.errors++;
    
    const errorInfo = {
      type,
      error: error?.message || String(error),
      stack: error?.stack,
      context,
      timestamp: new Date().toISOString()
    };
    
    this._addOperation({
      type: 'error',
      ...errorInfo
    });
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('[DateTimeTracker] Error:', errorInfo);
    }
  }
  
  /**
   * Add operation to history
   */
  _addOperation(operation) {
    this.stats.operations.push(operation);
    
    // Keep only last N operations
    if (this.stats.operations.length > this.maxOperations) {
      this.stats.operations.shift();
    }
  }
  
  /**
   * Get statistics
   */
  getStats() {
    const conversionTimes = this.performanceMetrics.conversionTimes;
    const formatTimes = this.performanceMetrics.formatTimes;
    const parseTimes = this.performanceMetrics.parseTimes;
    
    const calculatePercentile = (arr, percentile) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = Math.ceil((percentile / 100) * sorted.length) - 1;
      return sorted[Math.max(0, index)];
    };
    
    return {
      ...this.stats,
      performance: {
        conversions: {
          count: conversionTimes.length,
          avg: conversionTimes.length > 0 
            ? conversionTimes.reduce((a, b) => a + b, 0) / conversionTimes.length 
            : 0,
          p50: calculatePercentile(conversionTimes, 50),
          p95: calculatePercentile(conversionTimes, 95),
          p99: calculatePercentile(conversionTimes, 99)
        },
        formats: {
          count: formatTimes.length,
          avg: formatTimes.length > 0 
            ? formatTimes.reduce((a, b) => a + b, 0) / formatTimes.length 
            : 0,
          p50: calculatePercentile(formatTimes, 50),
          p95: calculatePercentile(formatTimes, 95),
          p99: calculatePercentile(formatTimes, 99)
        },
        parses: {
          count: parseTimes.length,
          avg: parseTimes.length > 0 
            ? parseTimes.reduce((a, b) => a + b, 0) / parseTimes.length 
            : 0,
          p50: calculatePercentile(parseTimes, 50),
          p95: calculatePercentile(parseTimes, 95),
          p99: calculatePercentile(parseTimes, 99)
        }
      },
      cache: {
        hits: this.stats.cacheHits,
        misses: this.stats.cacheMisses,
        hitRate: (this.stats.cacheHits + this.stats.cacheMisses) > 0
          ? this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses)
          : 0
      },
      errorRate: this.stats.conversions > 0
        ? this.stats.errors / this.stats.conversions
        : 0
    };
  }
  
  /**
   * Get recent operations
   */
  getRecentOperations(limit = 50) {
    return this.stats.operations.slice(-limit);
  }
  
  /**
   * Get errors only
   */
  getErrors() {
    return this.stats.operations.filter(op => op.type === 'error');
  }
  
  /**
   * Reset statistics
   */
  reset() {
    this.stats = {
      conversions: 0,
      errors: 0,
      timezoneDetections: 0,
      cacheHits: 0,
      cacheMisses: 0,
      operations: []
    };
    
    this.performanceMetrics = {
      conversionTimes: [],
      formatTimes: [],
      parseTimes: []
    };
  }
  
  /**
   * Enable/disable tracking
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  
  /**
   * Debug: Print statistics to console
   */
  debug() {
    console.log('[DateTimeTracker] Statistics:', this.getStats());
  }
}

// Singleton instance
let trackerInstance = null;

/**
 * Get tracker instance
 */
export function getTracker() {
  if (!trackerInstance) {
    trackerInstance = new DateTimeTracker();
  }
  return trackerInstance;
}

/**
 * Track conversion (convenience function)
 */
export function trackConversion(operation, duration, success = true, error = null) {
  getTracker().trackConversion(operation, duration, success, error);
}

/**
 * Track format (convenience function)
 */
export function trackFormat(operation, duration, success = true, error = null) {
  getTracker().trackFormat(operation, duration, success, error);
}

/**
 * Track parse (convenience function)
 */
export function trackParse(operation, duration, success = true, error = null) {
  getTracker().trackParse(operation, duration, success, error);
}

/**
 * Track timezone detection (convenience function)
 */
export function trackTimezoneDetection(timezone, method) {
  getTracker().trackTimezoneDetection(timezone, method);
}

/**
 * Track cache hit (convenience function)
 */
export function trackCacheHit(type) {
  getTracker().trackCacheHit(type);
}

/**
 * Track cache miss (convenience function)
 */
export function trackCacheMiss(type) {
  getTracker().trackCacheMiss(type);
}

/**
 * Track error (convenience function)
 */
export function trackError(type, error, context = {}) {
  getTracker().trackError(type, error, context);
}

export default getTracker();
